<template>
    <div class="Scenario">
        <v-layout class="scenario-layout">
            <div class="p1" :class="{active: activePanel1}">
                <ScenarioTree
                    v-if="rawData"
                    ref="TreeView"
                ></ScenarioTree>
            </div>
            <div style="flex: 1 1 1px; overflow: hidden">
                <div class="toolbar" v-if="rawData">
                    <h3 v-if="activeScenario">{{ activeScenario.name }}</h3>
                    <template v-if="activeScenarioCode && activeScenarioCode !== defaultScenarioCode">
                        <v-btn icon small @click="editScenarioName">
                            <v-icon small>mdi-pencil</v-icon>
                        </v-btn>
                        <v-btn icon small @click="deleteScenario">
                            <v-icon small>mdi-delete-outline</v-icon>
                        </v-btn>
                    </template>
                    <ScenarioError style="margin-left: 10px"></ScenarioError>
                    <v-flex></v-flex>
                    <div class="saving-status">
                        <v-icon>
                            {{ savingCount > 0 ? 'mdi-cloud-sync-outline' : 'mdi-cloud-check-outline' }}
                        </v-icon>
                    </div>
                    <ScenarioSearch style="min-width: 30%; margin-right: 10px;"></ScenarioSearch>
                    <ZoomButton></ZoomButton>
                    <v-flex></v-flex>
                    <v-btn color="primary" @click="openDebug">Debug</v-btn>
                    <v-btn outlined @click="togglePanel(1)" :class="{active: activePanel1}">
                        <img v-if="activePanel1" src="/img/scenario/side-bar-white.svg" alt="">
                        <img v-else src="/img/scenario/side-bar.svg" alt="">
                    </v-btn>
                    <v-btn outlined @click="togglePanel(3)" :class="{active: activePanel3}">
                        <img v-if="activePanel3" src="/img/scenario/window-white.svg" alt="">
                        <img v-else src="/img/scenario/window.svg" alt="">
                    </v-btn>
                    <v-btn outlined @click="togglePanel(4)" :class="{active: activePanel4}">
                        <img v-if="activePanel4" src="/img/scenario/chat-white.svg" alt="">
                        <img v-else src="/img/scenario/chat.svg" alt="">
                    </v-btn>
                    <v-btn color="primary" @click="openSetting">{{ $f.tc('setting') }}</v-btn>
                </div>
                <v-layout style="padding-bottom: 10px">
                    <ScenarioGraph
                        ref="ScenarioGraph"
                        class="p2"
                        :class="{active: activePanel2}"
                    ></ScenarioGraph>
                    <div class="p3" :class="{active: activePanel3}">
                        <StepContentEditor v-if="activeStep"></StepContentEditor>
                        <div v-else class="pa-3">
                            {{ $f.tc('pleaseSelectStep') }}
                        </div>
                    </div>
                    <div class="p4" :class="{active: activePanel4}">
                        <ScenarioChat
                            v-if="bot"
                            ref="ScenarioChat"
                            :active="activePanel4"
                        ></ScenarioChat>
                    </div>
                </v-layout>
            </div>
        </v-layout>
        <ScenarioDialogContainer></ScenarioDialogContainer>
    </div>
</template>

<script>
import { BotService, ScenarioService, WebViewService } from '@/services/bot.service'
import ScenarioGraph from '@/views/Scenario/Graph/ScenarioGraph'
import { GraphType, TreeSortType, TreeSortTypeIcon } from '@/utils/const'
import ZoomButton from '@/views/Scenario/ZoomButton'
import ScenarioSearch from '@/views/Scenario/Search/ScenarioSearch'
import { mapState } from 'vuex'
import ScenarioTree from '@/views/Scenario/ScenarioTree'
import StepContentEditor from '@/views/Scenario/StepEditor/StepContentEditor'
import { AppEventBus, AppEventBusType } from '@/eventbus/app.eventbus'
import ScenarioDialogContainer from '@/views/Scenario/ScenarioDialogContainer'
import ScenarioChat from '@/views/Scenario/ScenarioChat'
import ScenarioError from '@/views/Scenario/ScenarioError'
import FormRules from '@/modules/fptai/utils/FormRules'

export default {
    name: 'Scenario',
    components: {
        ScenarioError,
        ScenarioChat,
        ScenarioDialogContainer,
        StepContentEditor,
        ScenarioTree,
        ScenarioGraph,
        ScenarioSearch,
        ZoomButton
    },
    data () {
        return {
            GraphType,
            TreeSortType,
            TreeSortTypeIcon,
            rawData: null,
            // active panel
            activePanel1: true,
            activePanel2: true,
            activePanel3: false,
            activePanel4: false
        }
    },
    computed: {
        ...mapState({
            savingCount: state => state.scenario.savingCount,
            shouldUpdateGraph: state => state.scenario.shouldUpdateGraph,
            defaultScenarioCode: state => state.scenario.defaultScenarioCode,
            bot: state => state.bot,
            // active code
            activeScenarioCode: state => state.scenario.activeScenarioCode,
            activeStepCode: state => state.scenario.activeStepCode,
            activeCardCode: state => state.scenario.activeCardCode,
            // active instance
            activeScenario: state => state.scenario.activeScenario,
            activeStep: state => state.scenario.activeStep,
            activeCard: state => state.scenario.activeCard
        })
    },
    methods: {
        refresh () {
            let loadingCount = 2
            this.$appService.showLoading()
            let finalFn = () => {
                loadingCount--
                if (loadingCount < 1) {
                    this.$appService.hideLoading()
                }
            }
            // load webview list
            WebViewService.search(this.$route.params.botCode).then(result => {
                result.data.forEach(item => {
                    item.config = JSON.parse(item.config)
                })
                this.$store.dispatch(this.$at.scenario.WEB_VIEW_LIST, result.data)
            }).finally(finalFn)
            // supporter skill
            ScenarioService.getRequestSupportCategory(this.$route.params.botCode).then(result => {
                this.$store.dispatch(this.$at.scenario.SUPPORT_CATEGORY_LIST, result.data.request_support_categories)
            })
            // load data for scenario
            BotService.getAll(this.$route.params.botCode).then(result => {
                let data = result.data
                this.rawData = data
                for (let card of data.cards) {
                    card.config = JSON.parse(card.config)
                }
                this.createMapping(data)
                let activeStepCode = this.$route.query.step
                let activeCardCode = this.$route.query.card
                if (activeStepCode) {
                    this.$store.dispatch(this.$at.scenario.ACTIVE_DATA, { activeStepCode, activeCardCode })
                } else {
                    this.$store.dispatch(this.$at.scenario.ACTIVE_DATA, {
                        activeCardCode: null,
                        activeStepCode: null,
                        activeScenarioCode: this.$store.state.scenario.defaultScenarioCode
                    })
                }
                // check dialog act
                let actConfig = data.app_config.find(appConfig => appConfig.config_key === 'bot_act_domain')
                if (actConfig) {
                    BotService.getDialogAct(this.$route.params.botCode).then(result => {
                        this.$store.dispatch(this.$at.scenario.DIALOG_ACT, result.data)
                    })
                }
            }).finally(finalFn)
        },
        createMapping (data) {
            // scenario
            let scenarioList = data.scenarios
            const mapCodeToScenario = {}
            scenarioList.forEach(scenario => {
                scenario.steps = []
                mapCodeToScenario[scenario.code] = scenario
            })
            // step
            let mapCodeToStep = {}
            let mapNameToStep = {}
            let stepList = data.steps
            stepList.forEach(step => {
                let scenario = mapCodeToScenario[step.scenario_code]
                scenario.steps.push(step)
                step.cards = []
                mapCodeToStep[step.code] = step
                mapNameToStep[step.name] = step
            })
            // card
            let mapCodeToCard = {}
            let cardList = data.cards
            cardList.forEach(card => {
                mapCodeToCard[card.code] = card
                let step = mapCodeToStep[card.step_code]
                step.cards.push(card)
            })
            stepList.forEach(step => {
                step.cards.sort((a, b) => a.position > b.position ? 1 : -1)
            })
            // variable
            let variableList = data.scenario_memories.sort((a, b) => a.name.localeCompare(b.name))
            let variableTypeList = data.scenario_memory_types.sort((a, b) => a.name.localeCompare(b.name))
            let intentList = data.nlp.intents
            let entityList = data.nlp.entities
            let tagList = data.tags
            let appConfigList = data.app_config
            let crm = data.crm || {
                attributes: [],
                built_in: []
            }
            let mapping = {
                mapCodeToScenario,
                mapCodeToStep,
                mapNameToStep,
                mapCodeToCard,
                scenarioList,
                stepList,
                cardList,
                variableList,
                variableTypeList,
                tagList,
                appConfigList,
                crm,
                intentList,
                entityList
            }
            this.$store.dispatch(this.$at.scenario.MAPPING, mapping)
            return mapping
        },
        treeSortChange (type) {
            this.treeSortType = type
        },
        openSetting () {
            AppEventBus.emit(AppEventBusType.OPEN_SETTING)
        },
        openDebug () {
            window.location.href = `/bots/${this.bot.code}/debugger`
        },
        togglePanel (panel) {
            switch (panel) {
                case 1:
                    this.activePanel1 = !this.activePanel1
                    break
                case 2:
                    this.activePanel2 = !this.activePanel2
                    break
                case 3:
                    this.activePanel3 = !this.activePanel3
                    break
                case 4:
                    this.activePanel4 = !this.activePanel4
                    break
            }
        },
        editScenarioName () {
            this.$appService.useEventBus(AppEventBusType.EDIT_TEXT, {
                text: this.activeScenario.name,
                rules: [FormRules.ruleRequired, FormRules.ruleNoHtmlTag]
            }).then(name => {
                this.$store.dispatch(this.$at.scenario.UPDATE_SCENARIO, {
                    code: this.activeScenarioCode,
                    name: name
                })
            })
        },
        deleteScenario () {
            this.$appService.confirm(
                this.$f.tc('confirmDelete', { item: this.activeScenario.name })
            ).then(confirm => {
                if (confirm) {
                    this.$store.dispatch(this.$at.scenario.DELETE_SCENARIO, {
                        code: this.activeScenarioCode
                    })
                }
            })
        }
    },
    watch: {
        '$route.params.botCode': {
            immediate: true,
            handler: function () {
                this.refresh()
            }
        },
        activeStepCode () {
            if (this.activeStepCode) {
                this.activePanel3 = true
                if (this.$route.query.step !== this.activeStepCode) {
                    this.$router.push({ query: { step: this.activeStepCode } })
                }
            }
        }
    }
}
</script>
<style scoped lang="scss">
$borderColor: #F2F4FA;
$borderColor2: #0052CC;

.scenario-layout {
    background: $borderColor;

    .p1, .p2, .p3, .p4 {
        overflow: hidden;
        transition: all 0.3s ease;
    }

    .p2, .p3 {
        height: calc(100vh - 144px);
        border-radius: 5px;
        background: #fff;
    }

    .p1 {
        flex: 0 0 0;
        padding-right: 10px;
        background: $borderColor;
        height: calc(100vh - 64px);

        &.active {
            flex-basis: 270px;
        }
    }

    .p2 {
        flex: 0 0 0;
        margin-right: 0;

        &.active {
            flex: 1 0 25%;
            margin-right: 10px;
        }
    }

    .p3 {
        flex: 0 0 0;
        margin-right: 0;

        &.active {
            flex-basis: 577px;
            margin-right: 10px;
        }
    }

    .p4 {
        flex: 0 0 0;
        border-radius: 5px;

        &.active {
            flex-basis: 366px;
        }
    }
}

.toolbar {
    display: flex;
    padding: 0 10px;
    background: #fff;
    margin: 10px 0;
    align-items: center;
    height: 50px;
    border-radius: 5px;

    &::v-deep .v-btn {
        margin: 0 5px;
    }

    .v-btn {
        min-width: 0;
        padding: 0 10px;
    }
}

.saving-status {
    padding: 0 10px;
}
</style>
